import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useParams } from 'react-router-dom'
import { notification, Typography } from 'antd'
import clsx from 'clsx'
import useDirectory from 'hooks/useDirectory'
import useMutation from 'hooks/useMutation'
import usePoint from 'hooks/usePoint'
import { CurrentUser, Role } from 'models/User'
import MenuSetAnnotation from 'pages/building/panoramas/components/setAnnotation/MenuSetAnnotation'
import { useRecoilState, useRecoilValue } from 'recoil'
import notificationApiAtom from 'store/antdContextStore'
import {
  buildingModeState,
  listPointState,
  revalidatePointDetailState,
  selectedAreaState,
  SelectedMode,
  selectedPoint,
  selectedPointDetail,
  selectedSubImageState,
  setAnnotationModeState
} from 'store/buildingStore'
import { displayToggleState } from 'store/displayToggleStore'
import useSWR from 'swr'
import Krpano from 'utils/krpano'

import Button from 'components/atoms/Button'
import Modal from 'components/atoms/Modal'
import Title from 'components/atoms/Title'
import AnnotationIcon from 'components/icons/Annotation'
import CreateMemoIcon from 'components/icons/CreateMemoIcon'
import DirectionIcon from 'components/icons/DirectionIcon'
import LinkIcon from 'components/icons/LinkIcon'
import LockIcon from 'components/icons/LockIcon'
import PointMarkerIcon from 'components/icons/PointMarkerIcon'
import Preview360Icon from 'components/icons/Preview360'
import PreviewMapIcon from 'components/icons/PreviewMapIcon'
import StairIcon from 'components/icons/StairIcon'

import ModalFinishAllEditing from './ModalFinishAllEditing'

export default function PointActionsMenu() {
  const location = useLocation()
  const isTourMode = location.pathname.startsWith('/tour')
  const { t } = useTranslation()
  const { id: buildingId, floorId } = useParams()

  const { getBuiildingType } = useDirectory()

  const [isOpenModalConfirmCopy, setIsOpenModalConfirmCopy] = useState(false)

  const selectedSubImage = useRecoilValue(selectedSubImageState)
  const selectedArea = useRecoilValue(selectedAreaState)
  const currentSelectedPoint = useRecoilValue(selectedPoint)
  const points = useRecoilValue(listPointState)
  const pointDetail = useRecoilValue(selectedPointDetail)
  const displayToggle = useRecoilValue(displayToggleState)
  const [{ mode }, setAnnotationMode] = useRecoilState(setAnnotationModeState)
  const [buildingMode, setBuildingMode] = useRecoilState(buildingModeState)
  const [, setRevalidatePointDetail] = useRecoilState(
    revalidatePointDetailState
  )
  const notificationApi = useRecoilValue(notificationApiAtom)

  const { data: profile } = useSWR<CurrentUser | null>('v1/users/me', {
    revalidateOnMount: false
  })

  const { trigger: triggerCopyAnnotation, isMutating: isCopyAnnotation } =
    useMutation('/v1/annotations/copy-annotation')

  const { handleToggleLockOrUnLockPoint } = usePoint()

  const isHavingPoint = Boolean(points.length)
  const isHavingImage = Boolean(
    points.find((item) => item.id === currentSelectedPoint)?.image360
  )

  const isActive360 = useMemo(
    () =>
      Boolean(selectedArea === pointDetail?.areaId || buildingMode.previewMode),
    [buildingMode.previewMode, pointDetail?.areaId, selectedArea]
  )
  const isCheckLockedPoint = useMemo(
    () =>
      Boolean(pointDetail?.isLocked && profile?.id === pointDetail.lockedBy),
    [pointDetail?.isLocked, pointDetail?.lockedBy, profile?.id]
  )
  const isSubImageCopied = useMemo(
    () =>
      Boolean(
        pointDetail?.subImages?.find((item) => item.id === selectedSubImage)
          ?.isCopy
      ),
    [pointDetail?.subImages, selectedSubImage]
  )

  useEffect(() => {
    if (
      buildingMode.selectedMode === SelectedMode.ANNOTATION ||
      buildingMode.selectedMode === SelectedMode.TOUR_ANNOTATION
    ) {
      notificationApi?.success({
        message: t('translation.pleasePlacingAnnotation', {
          mode: buildingMode.selectedMode
        }),
        duration: 3
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildingMode.selectedMode])

  const handleActiveMode = (choosingMode: SelectedMode) => () => {
    if (!isHavingPoint && choosingMode !== SelectedMode.POINT) return

    if (
      choosingMode === SelectedMode.TOUR_ANNOTATION &&
      getBuiildingType() !== 10
    )
      return

    if (mode) {
      notification.destroy('placingSetAnnotation')
      setAnnotationMode((prev) => ({ ...prev, mode: undefined }))
    }

    if (mode === 'placingSetAnnotation') {
      Krpano.RemoveTempAnnotation?.()
      setBuildingMode((prev) => ({ ...prev, annotationPosition: [] }))
    }

    if (
      mode &&
      choosingMode !== SelectedMode.ANNOTATION &&
      choosingMode !== SelectedMode.TOUR_ANNOTATION
    ) {
      notification.destroy('placingAnnotation')
      setBuildingMode((prev) => ({
        ...prev,
        mode: undefined,
        selectedSetAnnotation: undefined,
        annotationId: undefined
      }))
    }

    if (buildingMode.selectedMode) {
      if (choosingMode === buildingMode.selectedMode) {
        setBuildingMode((prev) => ({
          ...prev,
          selectedMode: SelectedMode.NONE
        }))
        if (
          choosingMode === SelectedMode.ANNOTATION ||
          choosingMode === SelectedMode.TOUR_ANNOTATION
        ) {
          notification.destroy('placingAnnotation')
        }
        return
      }
      if (
        buildingMode.selectedMode === 'point' ||
        buildingMode.selectedMode === 'link'
      ) {
        setBuildingMode((prev) => ({ ...prev, selectedMode: choosingMode }))
        return
      }
      if (choosingMode !== buildingMode.selectedMode) {
        setBuildingMode((prev) => ({ ...prev, selectedMode: choosingMode }))
        return
      }
    }
    if (buildingMode.previewMode && !isCheckLockedPoint) return

    setBuildingMode((prev) => ({ ...prev, selectedMode: choosingMode }))
  }

  const handleChangePreviewMode = () => {
    const krpanoFull = document.getElementById('embedpano-full') as any
    const krpanoPreview = document.getElementById('embedpano-preview') as any

    if (buildingMode.previewMode) {
      const syncViewHlookat = krpanoFull?.get('view.hlookat')
      const syncViewVlookat = krpanoFull?.get('view.vlookat')
      const syncViewFov = krpanoFull?.get('view.fov')
      krpanoPreview?.call(`set(view.hlookat, ${syncViewHlookat})`)
      krpanoPreview?.call(`set(view.vlookat, ${syncViewVlookat})`)
      krpanoPreview?.call(`set(view.fov, ${syncViewFov})`)
    } else {
      const syncViewHlookat = krpanoPreview?.get('view.hlookat')
      const syncViewVlookat = krpanoPreview?.get('view.vlookat')
      const syncViewFov = krpanoPreview?.get('view.fov')
      krpanoFull?.call(`set(view.hlookat, ${syncViewHlookat})`)
      krpanoFull?.call(`set(view.vlookat, ${syncViewVlookat})`)
      krpanoFull?.call(`set(view.fov, ${syncViewFov})`)
    }
    setBuildingMode((prev) => ({ ...prev, previewMode: !prev.previewMode }))
  }

  const handlePreviewMode = () => {
    const isSwitchAble = currentSelectedPoint && isHavingImage && isActive360
    isSwitchAble && handleChangePreviewMode()
  }

  const handleReflectionIcon = () => {
    if (!isCopyAnnotation && !isSubImageCopied && isCheckLockedPoint) {
      const selectedSubImageDetail = pointDetail?.subImages?.find(
        (item) => item.id === selectedSubImage
      )
      const subImageInitView = selectedSubImageDetail?.initView

      triggerCopyAnnotation(
        {
          data: {
            point_id: currentSelectedPoint,
            building_id: +(buildingId || 0),
            floor_id: +(floorId || 0),
            sub_image_id: selectedSubImage,
            ...(subImageInitView
              ? {
                  sub_image_init_view: {
                    h: subImageInitView.h,
                    v: subImageInitView.v,
                    z: subImageInitView.z,
                    radar_h_gap: subImageInitView.radarHGap
                  }
                }
              : {})
          }
        },
        {
          onSuccess: () => {
            setIsOpenModalConfirmCopy(false)
            setRevalidatePointDetail(currentSelectedPoint)
          },
          onError: () => {
            setIsOpenModalConfirmCopy(false)
          }
        }
      )
    }
  }

  const isToggleAble = useMemo(
    () =>
      displayToggle.lockedBy === profile?.id ||
      profile?.role === Role.SA ||
      !displayToggle.isLocked,
    [displayToggle.isLocked, displayToggle.lockedBy, profile?.id, profile?.role]
  )

  const isUnpreviewAble = useMemo(
    () =>
      buildingMode.selectedMode ||
      !currentSelectedPoint ||
      !isHavingPoint ||
      !isHavingImage ||
      !isActive360,
    [
      buildingMode.selectedMode,
      currentSelectedPoint,
      isActive360,
      isHavingImage,
      isHavingPoint
    ]
  )

  if (buildingMode?.selectedMode === SelectedMode.DIRECTION) return null

  const rightClass = (() => {
    if (buildingMode?.previewMode) {
      return isTourMode ? 'right-[340px]' : 'right-[232px]'
    }
    return 'right-[203px]'
  })()
  const leftClass = (() => {
    if (buildingMode?.previewMode) {
      return isTourMode ? 'left-[340px]' : 'left-[232px]'
    }
    return 'left-[203px]'
  })()

  return (
    <>
      <div
        id="point-action-menu-container"
        className="absolute z-[500] top-5 left-1/2 -translate-x-1/2 select-none"
      >
        <div className={`absolute flex gap-[17px] ${rightClass}`}>
          {buildingMode?.previewMode ? (
            <div
              className={`flex rounded-full w-20 h-[60px] ${
                isToggleAble ? 'cursor-pointer' : 'cursor-not-allowed'
              } ${
                displayToggle.isLocked &&
                (profile?.id === displayToggle.lockedBy ||
                  profile?.role === Role.SA)
                  ? 'theme-bg-sub-primary'
                  : 'bg-[#021120CC]'
              }`}
              onClick={() => {
                isToggleAble && handleToggleLockOrUnLockPoint(profile?.id)
              }}
            >
              <div
                className={`w-full my-auto space-y-[6px] ${
                  !isToggleAble ? 'opacity-60' : ''
                }`}
              >
                <div className="ml-[31px]">
                  <LockIcon isSelfLocked />
                </div>
                <Typography className="text-white font-bold text-[10px] leading-[10px] text-center">
                  {pointDetail?.isLocked
                    ? t('building.finishEditing')
                    : t('building.startEditing')}
                </Typography>
              </div>
            </div>
          ) : null}

          <div
            className="flex rounded-full bg-[#021120CC] w-20 h-[60px] cursor-pointer"
            onClick={handlePreviewMode}
          >
            {buildingMode?.previewMode ? (
              <div className="m-auto space-y-[6px] text-center">
                <PreviewMapIcon />
                <Typography className="text-white font-bold text-[10px] leading-[10px]">
                  {t('building.map')}
                </Typography>
              </div>
            ) : (
              <div
                className={`m-auto space-y-[2px] text-center ${
                  isUnpreviewAble ? 'opacity-60' : ''
                }`}
              >
                <Preview360Icon />
                <Typography className="text-white font-bold text-[10px] leading-[10px]">
                  {t('building.360')}
                </Typography>
              </div>
            )}
          </div>
        </div>

        <div
          className={`flex py-1 justify-center ${
            buildingMode.previewMode ? 'gap-[18px]' : 'gap-10'
          } rounded-[30px] bg-[#021120CC] px-8 h-[60px]`}
        >
          {buildingMode?.previewMode ? (
            <>
              <div
                className={`flex flex-col items-center justify-center gap-1 h-full w-10 cursor-pointer ${
                  !isCheckLockedPoint ? 'opacity-60' : 'cursor-pointer'
                }`}
                onClick={handleActiveMode(SelectedMode.DIRECTION)}
              >
                <DirectionIcon />
                <Typography className="font-bold text-[10px] leading-[10px] text-white">
                  {t('building.initDirection')}
                </Typography>
              </div>
              <div
                className={`flex flex-col items-center justify-center gap-1 h-full w-10 cursor-pointer ${
                  !isCheckLockedPoint ? 'opacity-60' : 'cursor-pointer'
                }`}
                onClick={handleActiveMode(SelectedMode.STAIRWAY)}
              >
                <StairIcon
                  selected={buildingMode.selectedMode === SelectedMode.STAIRWAY}
                />
                <Typography className="font-bold text-[10px] leading-[10px] text-white">
                  {t('building.stairway')}
                </Typography>
              </div>
              <div
                className={`flex flex-col items-center justify-center gap-1 h-full w-10 cursor-pointer ${
                  !isCheckLockedPoint ? 'opacity-60' : 'cursor-pointer'
                }`}
                onClick={handleActiveMode(SelectedMode.ANNOTATION)}
              >
                <AnnotationIcon
                  selected={
                    buildingMode.selectedMode === SelectedMode.ANNOTATION
                  }
                />
                <Typography className="font-bold text-[10px] leading-[10px] text-white">
                  {t('building.annotation')}
                </Typography>
              </div>
              {getBuiildingType() === 10 && (
                <>
                  {/* Create tour anotation by one click */}
                  <div
                    className={`flex flex-col items-center justify-center gap-1 h-full pt-[6px] w-10 cursor-pointer ${
                      !isCheckLockedPoint ? 'opacity-60' : 'cursor-pointer'
                    }`}
                    onClick={handleActiveMode(SelectedMode.TOUR_ANNOTATION)}
                  >
                    <AnnotationIcon
                      selected={
                        buildingMode.selectedMode ===
                          SelectedMode.TOUR_ANNOTATION &&
                        getBuiildingType() === 10
                      }
                    />
                    <Typography className="font-bold text-[10px] leading-[10px] text-white text-center">
                      {t('building.tourAnnotation')}
                    </Typography>
                  </div>

                  {/* Create memo annotation with callout plugin by one click */}
                  <div
                    className={`flex flex-col items-center justify-center gap-1 h-full w-10 cursor-pointer ${
                      !isCheckLockedPoint ? 'opacity-60' : 'cursor-pointer'
                    }`}
                    onClick={handleActiveMode(
                      SelectedMode.TOUR_MEMO_ANNOTATION
                    )}
                  >
                    <CreateMemoIcon
                      selected={
                        buildingMode.selectedMode ===
                          SelectedMode.TOUR_MEMO_ANNOTATION &&
                        getBuiildingType() === 10
                      }
                    />
                    <Typography className="font-bold text-[10px] leading-[10px] text-white text-center">
                      {t('building.createMemoAnotation')}
                    </Typography>
                  </div>
                </>
              )}
            </>
          ) : (
            <>
              <div
                className={clsx(
                  'flex flex-col items-center justify-center gap-1 h-full w-10 cursor-pointer',
                  {
                    'point-selected': buildingMode?.selectedMode === 'point',
                    'point-normal': buildingMode?.selectedMode !== 'point'
                  }
                )}
                onClick={handleActiveMode(SelectedMode.POINT)}
              >
                <PointMarkerIcon
                  selected={buildingMode?.selectedMode === 'point'}
                />
                <Typography
                  className={`font-bold text-[10px] leading-[10px] ${
                    buildingMode?.selectedMode === 'point'
                      ? 'theme-text-sub-primary'
                      : 'text-white'
                  }`}
                >
                  {t('building.point')}
                </Typography>
              </div>

              <div
                className={clsx(
                  'flex flex-col items-center justify-center gap-1 h-full w-10 cursor-pointer',
                  {
                    'link-selected': buildingMode?.selectedMode === 'link',
                    'link-normal': buildingMode?.selectedMode !== 'link'
                  }
                )}
                onClick={handleActiveMode(SelectedMode.LINK)}
              >
                <LinkIcon selected={buildingMode?.selectedMode === 'link'} />
                <Typography
                  className={`font-bold text-[10px] leading-[10px] ${
                    buildingMode?.selectedMode === 'link'
                      ? 'theme-text-sub-primary'
                      : 'text-white'
                  }`}
                >
                  {t('building.link')}
                </Typography>
              </div>
            </>
          )}
        </div>

        <div className={`absolute ${leftClass} top-0 flex gap-[17px]`}>
          <MenuSetAnnotation isCheckLockedPoint={isCheckLockedPoint} />

          {buildingMode?.previewMode && selectedSubImage && (
            <div
              className={`flex items-center text-center px-2 rounded-[30px] bg-[#021120CC] w-20 h-[60px] ${
                isCheckLockedPoint && !isSubImageCopied
                  ? 'cursor-pointer'
                  : 'cursor-not-allowed'
              }`}
              onClick={() =>
                isCheckLockedPoint &&
                !isSubImageCopied &&
                setIsOpenModalConfirmCopy(true)
              }
            >
              <Typography
                className={`font-bold text-[10px] leading-[10px] text-white ${
                  isCheckLockedPoint && !isSubImageCopied ? '' : 'opacity-60'
                }`}
              >
                アイコン反映
              </Typography>
            </div>
          )}

          {buildingMode?.previewMode && profile?.role === Role.SA ? (
            <ModalFinishAllEditing />
          ) : null}
        </div>
      </div>

      <Modal open={isOpenModalConfirmCopy} width={480}>
        <Title title="アイコン反映" bottomMargin="mb-8" />

        <div className="flex flex-col gap-10 text-center">
          <Typography className="text-[14px] leading-[21px]">
            メイン画像の注釈をコピーしますか
          </Typography>

          <div className="flex gap-10 justify-center">
            <Button
              sizing="w-[140px] h-[50px]"
              onClick={() => setIsOpenModalConfirmCopy(false)}
            >
              {t('building.buttonCancel')}
            </Button>

            <Button
              loading={isCopyAnnotation}
              type="primary"
              sizing="w-[140px] h-[50px]"
              onClick={handleReflectionIcon}
            >
              {t('building.reflectAnnotation')}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  )
}
